import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import {
  forecastingHorizonDaysSchema,
  forecastingMerchantsSchema,
  forecastingTimelineGroupingValues,
} from "./get-forecasting-accuracy-timeline";

/**
 * Signed-percent-error bin edges, as fractions: 12 bins of 25 points from
 * −100% (a forecast can't be lower than −100% of actuals) to +200%, plus a
 * 13th overflow bin for ≥ +200%.
 *
 * Percent error = (forecast total − actual total) / actual total for the
 * horizon window; positive = over-forecast. sku-origin-dates with zero
 * actuals get dedicated placement instead of being dropped: forecast > 0 is
 * pure over-forecast and lands in the overflow bin, while forecast = 0 was
 * trivially right and is reported via trivialCount rather than binned —
 * dead SKUs dominate low-seller rows (~97% for large catalogs) and would
 * flatten every informative bar.
 */
export const forecastingHistogramBinEdges = [
  -1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2,
] as const;

/** 12 edge-bounded bins plus the ≥ +200% overflow bin */
export const FORECASTING_HISTOGRAM_BIN_COUNT =
  forecastingHistogramBinEdges.length;

export const forecastingErrorHistogramSchema = z.object({
  label: z.string(),
  /** Binned sku-origin-date counts, length FORECASTING_HISTOGRAM_BIN_COUNT */
  bins: z.array(z.number()),
  /** Units charted in bins (excludes trivialCount) */
  totalCount: z.number(),
  /** sku-origin-dates with no sales and no forecast, not charted */
  trivialCount: z.number(),
});

export type ForecastingErrorHistogram = z.infer<
  typeof forecastingErrorHistogramSchema
>;

export const getForecastingErrorHistogram = {
  name: "getForecastingErrorHistogram" as const,
  input: z.object({
    /** Null loads only the merchant list */
    teamId: z.string().nullable(),
    horizonDays: forecastingHorizonDaysSchema,
    /** Same grouping semantics as the timeline: all | volume | sku */
    grouping: z.enum(forecastingTimelineGroupingValues),
    /** Required series filter when grouping is "sku"; ignored otherwise */
    sku: z.string().nullable(),
    /** Inclusive origin-date bounds; null = unbounded on that side */
    originStart: zExt.plainDate().nullable(),
    originEnd: zExt.plainDate().nullable(),
  }),
  output: z.object({
    /** IMS merchants with forecast runs, for the merchant selector */
    merchants: forecastingMerchantsSchema,
    /**
     * Scored SKUs for the requested merchant, for the SKU selector. Empty
     * unless grouping is "sku" and a merchant is selected.
     */
    skus: z.array(z.string()),
    /**
     * Completed-origin bounds for the selected merchant and horizon (windows
     * fully elapsed), for the range picker. Null when none exist.
     */
    originRange: z
      .object({ min: zExt.plainDate(), max: zExt.plainDate() })
      .nullable(),
    /**
     * One histogram per charted group: one for "all"/"sku" grouping, high
     * sellers then low sellers for "volume". Each unit in a bin is one
     * sku-origin-date whose signed percent error falls in that bin.
     */
    histograms: z.array(forecastingErrorHistogramSchema),
  }),
};
